import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Truck, Building2, Landmark, CheckCircle2, ArrowRight,
  Zap, Battery, Wifi, Settings, Shield, BarChart3
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CTABanner } from '../components/shared/CTABanner';
import { SectionHeader } from '../components/shared/SectionHeader';

const solutions = [
  {
    id: 'fleet',
    icon: Truck,
    title: 'Fleet & Depot Charging',
    tagline: 'Design and build high-uptime depot charging for light, medium, and heavy-duty fleets.',
    challenges: [
      'Complex depot layouts requiring optimized charger placement',
      'Mixed vehicle types with different charging requirements',
      'Peak demand management and utility coordination',
      'Integration with fleet management systems'
    ],
    offerings: [
      'Depot charging design & planning',
      'Route-based charge modeling',
      'Software-driven load management',
      'Mixed-vehicle charging configurations',
      'Energy analytics & performance dashboards',
      'Integration with telematics & fleet management systems'
    ],
    scope: {
      power: '50kW - 350kW DC Fast Chargers',
      types: 'Level 2 AC & DC Fast Charging',
      services: 'Design, Build, Operate'
    },
    image: 'https://images.unsplash.com/photo-1739105685005-466d690e9c0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxlbGVjdHJpYyUyMGJ1cyUyMGZsZWV0JTIwZGVwb3R8ZW58MHx8fHwxNzc0MTE4NjQwfDA&ixlib=rb-4.1.0&q=85'
  },
  {
    id: 'commercial',
    icon: Building2,
    title: 'Commercial & Retail',
    tagline: 'EV charging as an amenity and revenue stream for malls, offices, and mixed-use properties.',
    challenges: [
      'Balancing cost with revenue opportunity',
      'Managing charging demand across multiple tenants',
      'Creating seamless customer experience',
      'Maximizing property value and foot traffic'
    ],
    offerings: [
      'Zero-cost installation via CaaS',
      'Revenue-sharing programs',
      'Increased visitor traffic',
      'Modern amenity for customers',
      'Smart load balancing and energy optimization',
      'Co-branding opportunities with Skytec EV'
    ],
    scope: {
      power: '7kW - 150kW',
      types: 'Level 2 AC & DC Fast Charging',
      services: 'CaaS, Build, Maintain'
    },
    image: 'https://images.pexels.com/photos/4678065/pexels-photo-4678065.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  },
  {
    id: 'municipal',
    icon: Landmark,
    title: 'Utilities & Municipalities',
    tagline: 'Grid-aware planning, permitting, and coordination with utility stakeholders.',
    challenges: [
      'Complex permitting and regulatory requirements',
      'Grid capacity constraints',
      'Public accessibility and ADA compliance',
      'Long-term scalability planning'
    ],
    offerings: [
      'Grid-aware planning and design',
      'Utility coordination and interconnection',
      'ADA-compliant site layouts',
      'Expandable infrastructure design',
      'Public charging network deployment',
      'Renewable integration readiness'
    ],
    scope: {
      power: '50kW - 350kW DC Fast Chargers',
      types: 'Public DC Fast Charging Hubs',
      services: 'Full Turnkey Delivery'
    },
    image: 'https://images.unsplash.com/photo-1760246964044-1384f71665b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBidWlsZGluZyUyMGV4dGVyaW9yfGVufDB8fHx8MTc3NDExODY0M3ww&ixlib=rb-4.1.0&q=85'
  }
];

const processSteps = [
  { icon: Settings, title: 'Site Evaluation & Load Assessment', description: 'Comprehensive analysis of your site, electrical capacity, and operational needs.' },
  { icon: Zap, title: 'Electrical & Civil Engineering Design', description: 'Detailed engineering plans including power distribution and ADA-compliant layouts.' },
  { icon: Shield, title: 'Permitting & Utility Coordination', description: 'Navigate regulatory requirements and utility interconnection processes.' },
  { icon: Battery, title: 'Construction & Installation', description: 'Professional installation with minimal disruption to your operations.' },
  { icon: BarChart3, title: 'Managed Charging & Support', description: 'Ongoing monitoring, maintenance, and operational support.' }
];

export const SolutionsPage = () => {
  const location = useLocation();

  useEffect(() => {
    // Handle hash navigation
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <main className="pt-20" data-testid="solutions-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-slate-50 circuit-pattern" data-testid="solutions-hero">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2B5] mb-4 block">
              Solutions
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 mb-6">
              Turnkey EV Infrastructure for{' '}
              <span className="gradient-text">Complex Deployments</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Skytec EV delivers turnkey EV infrastructure for complex, multi-site deployments. From design to operations, we handle everything.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solutions Tabs */}
      <section className="py-20 md:py-28 bg-white" data-testid="solutions-tabs">
        <div className="container-custom">
          <Tabs defaultValue="fleet" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-12 bg-slate-100 p-1 rounded-xl h-auto">
              {solutions.map((solution) => (
                <TabsTrigger
                  key={solution.id}
                  value={solution.id}
                  id={solution.id}
                  className="flex items-center gap-2 py-4 px-6 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold text-sm"
                  data-testid={`tab-${solution.id}`}
                >
                  <solution.icon className="w-5 h-5" />
                  <span className="hidden sm:inline">{solution.title}</span>
                  <span className="sm:hidden">{solution.title.split(' ')[0]}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {solutions.map((solution) => (
              <TabsContent key={solution.id} value={solution.id} className="mt-0">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="grid lg:grid-cols-2 gap-12 items-start"
                >
                  {/* Content */}
                  <div>
                    <div className="mb-8">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00A2B5] to-[#36D890] flex items-center justify-center mb-6 shadow-lg">
                        <solution.icon className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="text-3xl font-black text-slate-900 mb-4">{solution.title}</h2>
                      <p className="text-lg text-slate-600">{solution.tagline}</p>
                    </div>

                    {/* Challenges */}
                    <div className="mb-8">
                      <h3 className="text-lg font-bold text-slate-900 mb-4">Challenges We Solve</h3>
                      <ul className="space-y-3">
                        {solution.challenges.map((challenge, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-slate-600">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#00A2B5] mt-2 flex-shrink-0" />
                            {challenge}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Offerings */}
                    <div className="mb-8">
                      <h3 className="text-lg font-bold text-slate-900 mb-4">Our Solutions</h3>
                      <ul className="space-y-3">
                        {solution.offerings.map((offering, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-[#00C5A5] flex-shrink-0 mt-0.5" />
                            <span className="text-slate-700">{offering}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Scope */}
                    <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                      <h3 className="text-lg font-bold text-slate-900 mb-4">Typical Scope</h3>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs font-bold uppercase text-slate-500 mb-1">Power Range</p>
                          <p className="text-sm text-slate-700">{solution.scope.power}</p>
                        </div>
                        <div>
                          <p className="text-xs font-bold uppercase text-slate-500 mb-1">Charger Types</p>
                          <p className="text-sm text-slate-700">{solution.scope.types}</p>
                        </div>
                        <div>
                          <p className="text-xs font-bold uppercase text-slate-500 mb-1">Services</p>
                          <p className="text-sm text-slate-700">{solution.scope.services}</p>
                        </div>
                      </div>
                    </div>

                    <Link
                      to="/contact"
                      className="btn-gradient inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold mt-8 group"
                      data-testid={`cta-${solution.id}`}
                    >
                      Get a Custom Proposal
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>

                  {/* Image */}
                  <div className="relative">
                    <div className="rounded-2xl overflow-hidden shadow-xl">
                      <img 
                        src={solution.image} 
                        alt={solution.title}
                        className="w-full h-auto object-cover aspect-[4/3]"
                      />
                    </div>
                    <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-gradient-to-br from-[#00A2B5]/20 to-[#36D890]/20 rounded-full blur-3xl" />
                  </div>
                </motion.div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="solutions-process">
        <div className="container-custom">
          <SectionHeader
            overline="Our Process"
            title="End-to-End Delivery Model"
            centered
          />

          <div className="grid md:grid-cols-5 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                {/* Connector line */}
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-[#00A2B5] to-[#36D890] opacity-30" />
                )}
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-white border border-slate-200 flex items-center justify-center mx-auto mb-4 shadow-sm">
                    <step.icon className="w-8 h-8 text-[#00A2B5]" />
                  </div>
                  <span className="text-xs font-bold text-[#00A2B5] mb-2 block">Step {index + 1}</span>
                  <h3 className="text-sm font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-xs text-slate-600 leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CaaS Section */}
      <section id="caas" className="py-20 md:py-28 bg-white" data-testid="caas-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2B5] mb-4 block">
                Charging-as-a-Service
              </span>
              <h2 className="text-3xl md:text-4xl font-black tracking-tight text-slate-900 mb-6">
                Zero-CapEx EV Infrastructure
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Skytec EV's CaaS model lets organizations electrify without upfront capital costs. We design, fund, install, and operate your entire charging system — you simply pay a predictable monthly rate.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {[
                  'Zero-CapEx charging infrastructure',
                  '24/7 monitoring & real-time diagnostics',
                  'Full maintenance, repairs, and upgrades',
                  'Network management & data analytics',
                  'Energy optimization to reduce costs',
                  'Guaranteed uptime & SLAs'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#00C5A5] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/contact"
                className="btn-gradient inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold group"
                data-testid="caas-cta"
              >
                Get Your CaaS Proposal
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-slate-50 rounded-2xl p-8 border border-slate-200"
            >
              <h3 className="text-xl font-bold text-slate-900 mb-6">CaaS vs Traditional CapEx</h3>
              <div className="space-y-6">
                <div className="grid grid-cols-3 gap-4 text-sm font-bold text-slate-500 border-b border-slate-200 pb-4">
                  <span>Feature</span>
                  <span className="text-center">CaaS</span>
                  <span className="text-center">Traditional</span>
                </div>
                {[
                  { feature: 'Upfront Cost', caas: '$0', traditional: '$50k-500k+' },
                  { feature: 'Maintenance', caas: 'Included', traditional: 'Your Cost' },
                  { feature: 'Monitoring', caas: '24/7', traditional: 'Self-managed' },
                  { feature: 'Upgrades', caas: 'Included', traditional: 'Additional Cost' },
                  { feature: 'Risk', caas: 'Skytec', traditional: 'Customer' }
                ].map((row, idx) => (
                  <div key={idx} className="grid grid-cols-3 gap-4 text-sm items-center">
                    <span className="text-slate-700">{row.feature}</span>
                    <span className="text-center text-[#00C5A5] font-semibold">{row.caas}</span>
                    <span className="text-center text-slate-500">{row.traditional}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <CTABanner 
        title="Ready to Electrify?"
        subtitle="Tell us about your sites, timelines, and constraints—we'll respond with a clear, actionable plan."
      />
    </main>
  );
};

export default SolutionsPage;
